// src/shoppingFlow/OrderConfirmationPage.js
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';

function fmt(n, c = 'CAD') {
  const v = Number(n ?? 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
}

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // who am I? (decides whether to show “View orders” link)
        const meRes = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
        if (alive) setUser(meRes.ok ? await meRes.json() : null);
      } catch {}

      try {
        const res = await fetch(`${API_BASE}/api/orders/${orderId}`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load order.');
        if (alive) setOrder(data);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load order.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-gray-50" data-qa="order-confirmation-page">
      <header className="bg-white shadow">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent"
          >
            SodaSips
          </Link>
          <div className="text-sm text-gray-500">Order #{orderId}</div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <QaAlert
          id="order-confirmation-alert"
          dataQa="qa-order-confirmation-alert"
          message={error}
          variant="error"
        />

        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : (
          <div className="bg-white rounded-xl shadow p-6">
            <h1 className="text-2xl font-bold text-gray-800">Thank you for your order!</h1>
            <p className="text-gray-600 mt-1">
              Your order <span className="font-semibold">#{orderId}</span> has been received.
            </p>

            {/* Tracking */}
            {(order?.tracking_number || order?.shipping_status) && (
              <section className="mt-4 p-4 bg-gray-50 rounded-lg" data-qa="order-tracking">
                <h2 className="text-lg font-semibold mb-2">Tracking</h2>

                {order?.tracking_number ? (
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-gray-800" data-qa="order-tracking-number">
                      {order.tracking_number}
                    </span>
                    <button
                      onClick={() => navigator.clipboard?.writeText(order.tracking_number)}
                      className="text-sm px-2 py-1 border rounded hover:bg-gray-100"
                      data-qa="order-tracking-copy"
                    >
                      Copy
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">Assigning tracking number…</div>
                )}

                <p className="text-sm text-gray-500 mt-1">
                  Status:{' '}
                  <span data-qa="order-shipping-status">
                    {order?.shipping_status || 'Processing'}
                  </span>
                </p>
              </section>
            )}

            {/* Items & Summary */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <section className="md:col-span-2">
                <h2 className="text-lg font-semibold mb-3">Items</h2>
                <div className="divide-y">
                  {(order?.items || []).map((it) => (
                    <div
                      key={it.id}
                      className="py-3 flex items-center justify-between"
                      data-qa="order-item"
                    >
                      <div>
                        <div className="font-medium text-gray-800">
                          {it.product_name_snapshot}
                        </div>
                        <div className="text-sm text-gray-500">
                          {it.variant_pack_size || '—'}
                        </div>
                        <div className="text-sm text-gray-500">Qty: {it.quantity}</div>
                      </div>
                      <div className="font-semibold">
                        {fmt(Number(it.unit_price) * Number(it.quantity), order?.currency)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <aside className="bg-gray-50 rounded-lg p-4 h-fit">
                <h2 className="text-lg font-semibold mb-3">Order Summary</h2>

                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{fmt(order?.subtotal, order?.currency)}</span>
                </div>

                {Number(order?.discount_amount) > 0 && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Discount</span>
                    <span className="text-green-700">
                      - {fmt(order?.discount_amount, order?.currency)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Tax</span>
                  <span>{fmt(order?.tax_amount, order?.currency)}</span>
                </div>

                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Shipping</span>
                  <span>{fmt(order?.shipping_fee, order?.currency)}</span>
                </div>

                <div className="border-t my-2" />

                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>{fmt(order?.total_price, order?.currency)}</span>
                </div>
              </aside>
            </div>

            {/* Shipping Address */}
            <section className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Shipping To</h2>
              <p className="text-gray-700">
                {order?.shipping_name}
                <br />
                {order?.shipping_address1}
                {order?.shipping_address2 ? `, ${order.shipping_address2}` : ''}
                <br />
                {order?.shipping_city}, {order?.shipping_region} {order?.shipping_postal_code}
                <br />
                {order?.shipping_country}
              </p>
            </section>

            {/* Actions */}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              >
                Continue Shopping
              </Link>
              {user && (
                <Link
                  to="/orders"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  View Order History
                </Link>
              )}
              <button
                onClick={() => window.print()}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Print
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
